import { CopyIcon } from '@phosphor-icons/react'
import { parseISO } from 'date-fns'
import { useTranslation } from 'react-i18next'

import { Page } from '@/components/layout/page'
import { PageHeader } from '@/components/layout/page-header'
import { Button } from '@/components/ui/button'
import { EmptyState } from '@/components/ui/empty-state'
import { Icon } from '@/components/ui/icon'
import { toastFactory } from '@/factories'
import { getPathString } from '@/factories/create-router-factory'
import { setStoredDesignVersion } from '@/hooks/use-design-version-toggle'
import { formatDateOnly } from '@/utils/date'

import { BETA_PAGES, JIRA_BROWSE_URL } from './beta.constants'
import * as S from './beta.styled'
import type { BetaPage as BetaPageItem } from './beta.types'
import { getBetaPageUrl, groupBetaPages } from './beta.utils'

export const BetaPage = () => {
  const { t } = useTranslation(['beta', 'navbar'])

  const handleOpen = (page: BetaPageItem) => {
    if (page.designGroup) setStoredDesignVersion(page.designGroup, 'new')
  }

  const handleCopy = async (page: BetaPageItem) => {
    try {
      await navigator.clipboard.writeText(getBetaPageUrl(page.to))
      toastFactory.success(t('beta:page.copy.success'))
    } catch {
      toastFactory.error(t('beta:page.copy.error'))
    }
  }

  return (
    <Page centered fillHeight={false}>
      <PageHeader title={t('beta:page.title')} />
      {BETA_PAGES.length === 0 ? (
        <EmptyState
          title={t('beta:page.empty.title')}
          description={t('beta:page.empty.description')}
        />
      ) : (
        <S.Container>
          {groupBetaPages(BETA_PAGES).map(({ group, pages: groupPages }) => (
            <S.Group key={group}>
              <S.GroupTitle>{t(`navbar:items.${group}`)}</S.GroupTitle>
              <S.List>
                {groupPages.map((page) => (
                  <S.Item key={page.id}>
                    <S.Name>
                      <S.NameLink
                        to={getPathString(page.to)}
                        onAuxClick={() => handleOpen(page)}
                        onClick={() => handleOpen(page)}
                      >
                        {page.name}
                      </S.NameLink>
                    </S.Name>
                    {(page.jira || page.releaseDate) && (
                      <S.Meta>
                        {page.jira && (
                          <S.ExternalLink
                            href={`${JIRA_BROWSE_URL}/${page.jira}`}
                            rel="noopener noreferrer"
                            target="_blank"
                          >
                            {page.jira}
                          </S.ExternalLink>
                        )}
                        {page.releaseDate && (
                          <span>
                            {t('beta:page.releaseDate', {
                              date: formatDateOnly(parseISO(page.releaseDate)),
                            })}
                          </span>
                        )}
                      </S.Meta>
                    )}
                    <S.CopyWrapper>
                      <Button
                        onlyBox
                        use="secondary"
                        size="small"
                        title={t('beta:page.copy.label')}
                        aria-label={t('beta:page.copy.label')}
                        onClick={() => handleCopy(page)}
                      >
                        <Icon icon={CopyIcon} />
                      </Button>
                    </S.CopyWrapper>
                  </S.Item>
                ))}
              </S.List>
            </S.Group>
          ))}
        </S.Container>
      )}
    </Page>
  )
}
